import {
  alpha, createTheme, darken, getContrastRatio, Theme,
} from '@mui/material/styles';

import { FinderConfig } from '../types/finderConfig';
import { COLOR_TOKENS, COLOR_TOKENS_DARK } from './tokens';

export type ColorMode = 'light' | 'dark';

const WHITE = '#fff';
const AA_CONTRAST = 4.5;

// Darken the brand color until white text meets WCAG AA; dark colors pass as-is.
const darkenForWhiteText = (color: string): string => {
  let result = color;
  for (let amount = 0; amount <= 0.9; amount += 0.05) {
    result = amount === 0 ? color : darken(color, amount);
    if (getContrastRatio(result, WHITE) >= AA_CONTRAST) break;
  }
  return result;
};

/** Per-org MUI theme. Brand colors from finder_configs; brand text stays white (WCAG AA). */
export function buildTheme(config: FinderConfig, mode: ColorMode = 'light'): Theme {
  const primary = darkenForWhiteText(config.primaryColor || COLOR_TOKENS.primary);
  const secondary = darkenForWhiteText(config.secondaryColor || COLOR_TOKENS.secondary);

  // Org primary/secondary/error carry over; only the neutral surfaces flip in dark.
  const neutrals = mode === 'dark' ? COLOR_TOKENS_DARK : COLOR_TOKENS;

  return createTheme({
    palette: {
      mode,
      contrastThreshold: 4.5,
      primary: { main: primary, contrastText: WHITE },
      secondary: { main: secondary, contrastText: WHITE },
      background: {
        default: neutrals.backgroundDefault,
        paper: neutrals.backgroundPaper,
      },
      text: {
        primary: neutrals.textPrimary,
        secondary: neutrals.textSecondary,
      },
      divider: neutrals.divider,
      error: { main: COLOR_TOKENS.error },
    },
    shape: { borderRadius: COLOR_TOKENS.borderRadius },
    typography: {
      // Inter across all weights (loaded in index.html).
      fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
      h1: { fontSize: '1.875rem', fontWeight: 700 }, // text-3xl / 30px
      h2: { fontSize: '1.5rem', fontWeight: 600 }, // text-2xl / 24px
      h3: { fontSize: '1.25rem', fontWeight: 600 }, // text-xl / 20px
      button: { textTransform: 'none', fontWeight: 500 }, // font-medium
    },
    components: {
      // 6px radius, 14px/medium text, 36/40/44 heights (sm/md/lg).
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 6, fontSize: '0.875rem' },
          sizeSmall: { minHeight: 36, paddingLeft: 12, paddingRight: 12 },
          sizeMedium: { minHeight: 40, paddingLeft: 16, paddingRight: 16 },
          sizeLarge: { minHeight: 44, paddingLeft: 32, paddingRight: 32 },
        },
      },
      // 6px input radius.
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 6 } },
      },
      // Full pill, 12px, semibold.
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 9999, fontSize: '0.75rem', fontWeight: 600 },
        },
      },
      // Black surface, 6px radius, 12px text.
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
            borderRadius: 6,
            padding: '6px 12px',
            fontSize: '0.75rem',
            fontWeight: 500,
          }),
          arrow: ({ theme }) => ({ color: theme.palette.common.black }),
        },
      },
      // Outlined Paper is the card surface: 8px radius, border, subtle shadow.
      MuiPaper: {
        styleOverrides: {
          outlined: { boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' },
        },
      },
      // Selected dropdown item: brand tint + left accent (inset shadow so it
      // adds no layout width). Hover styling is left to MUI defaults.
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
              boxShadow: `inset 3px 0 0 0 ${theme.palette.primary.main}`,
            },
          }),
        },
      },
    },
  });
}
